import { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "./ui/dialog";
import { Loader2, Globe, Percent } from "lucide-react";
import { api, formatApiErrorDetail } from "../lib/api";
import { toast } from "sonner";
import { useAuth } from "../lib/auth";
import { useT } from "../lib/i18n";

/**
 * One-time onboarding dialog. Mounts at the top of `App` (when an authenticated user has
 * `onboarding_complete !== true`). Collects the rep's preferred language + their store's
 * sales-tax percentage, then PATCHes them onto the user record.
 *
 * Defaults: language = current i18n.lang, sales tax = 0%.
 * The dialog blocks navigation until the rep saves it (no "skip"), per product spec —
 * we want every rep to set these once.
 */
const LANGUAGES = [
  { value: "en", label: "English" },
  { value: "es", label: "Español" },
  { value: "ar", label: "العربية" },
];

export default function OnboardingDialog() {
  const { user, refresh } = useAuth();
  const { lang, setLang } = useT();
  const open = !!user && user.onboarding_complete !== true;

  const [language, setLanguage] = useState(user?.default_language || lang || "en");
  const [taxPct, setTaxPct] = useState(
    user?.default_sales_tax_pct != null ? String(user.default_sales_tax_pct) : ""
  );
  const [busy, setBusy] = useState(false);

  if (!open) return null;

  const submit = async (e) => {
    e?.preventDefault?.();
    const tax = parseFloat(taxPct);
    if (Number.isNaN(tax) || tax < 0 || tax > 50) {
      toast.error("Enter a sales tax between 0 and 50%.");
      return;
    }
    setBusy(true);
    try {
      await api.patch("/auth/me/preferences", {
        default_language: language,
        default_sales_tax_pct: tax,
        onboarding_complete: true,
      });
      setLang(language);
      await refresh();
      toast.success("Preferences saved");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={() => { /* blocking — handled by submit */ }}>
      <DialogContent className="rounded-none max-w-md" data-testid="onboarding-dialog">
        <DialogHeader>
          <DialogTitle className="text-xl font-bold text-[#0B132B]">
            Welcome{user?.name ? `, ${user.name.split(" ")[0]}` : ""} —
            <span className="block text-sm text-slate-500 font-normal mt-1">A couple of quick defaults before you start.</span>
          </DialogTitle>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-5 pt-2">
          <div>
            <div className="tw-overline mb-2 flex items-center gap-2"><Globe className="h-3.5 w-3.5" /> Default language</div>
            <p className="text-xs text-slate-500 mb-2">Used for the admin UI and public estimator pages. You can override the language per-print when sharing a quote with a customer.</p>
            <div className="grid grid-cols-3 gap-2">
              {LANGUAGES.map((opt) => (
                <button
                  type="button"
                  key={opt.value}
                  onClick={() => setLanguage(opt.value)}
                  className={`border-2 px-3 py-2 text-sm font-bold tracking-wide ${
                    language === opt.value
                      ? "border-[#0B132B] bg-[#0B132B] text-white"
                      : "border-slate-300 text-slate-600 hover:border-[#0B132B]"
                  }`}
                  data-testid={`onboarding-lang-${opt.value}`}
                >
                  {opt.label}
                </button>
              ))}
            </div>
          </div>

          <div>
            <div className="tw-overline mb-2 flex items-center gap-2"><Percent className="h-3.5 w-3.5" /> Default sales tax %</div>
            <p className="text-xs text-slate-500 mb-2">Pre-fills "Due today" calculations on quotes you build. Use your store's local rate.</p>
            <input
              type="number"
              step="0.01"
              min="0"
              max="50"
              required
              className="input-sharp"
              placeholder="e.g. 6.0"
              value={taxPct}
              onChange={(e) => setTaxPct(e.target.value)}
              data-testid="onboarding-sales-tax"
              autoFocus
            />
          </div>

          <DialogFooter>
            <button
              type="submit"
              className="btn-brand disabled:opacity-40"
              disabled={busy || !taxPct.trim()}
              data-testid="onboarding-submit"
            >
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : null} Save & continue
            </button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
